// web/src/console/sections/TrafficSection.tsx
import type { JSX } from "react";
import { adminApi } from "../../lib/adminApi";
import { useConsole, RANGE_MS } from "../ConsoleContext";
import { useAsync } from "../useAsync";
import { LineChart, RangeOverview, StackedBar, BarChart, Heatmap, StatCard } from "../charts";
import { CardTitle } from "../Term";
import { fmtCount, fmtMs, fmtBucketUnit } from "../format";

// Status-code classes shown under the 状态码 chart, with the same tones as the
// stacked bars so the legend reads as a key: 2xx light green (healthy), 4xx
// amber, 5xx red. Keep in sync with StackedBar's TONE map.
const STATUS_LEGEND = [
  { key: "c2xx", color: "color-mix(in srgb, var(--color-success) 55%, var(--bg-raised))", label: "2xx 成功" },
  { key: "c4xx", color: "var(--color-warning)", label: "4xx 客户端错误" },
  { key: "c5xx", color: "var(--color-error)", label: "5xx 服务端错误" }
] as const;

const summaryStyle = {
  fontSize: "var(--text-caption)",
  color: "var(--text-tertiary)",
  margin: "2px 0 var(--space-2)",
  fontVariantNumeric: "tabular-nums"
} as const;

export function TrafficSection(): JSX.Element {
  const { query, range, win, setWin, effectiveWindow, tick } = useConsole();
  const { data, loading, error, reload } = useAsync(
    () => adminApi.traffic({ range: query.range, ...(query.from !== undefined && query.to !== undefined ? { from: query.from, to: query.to } : {}) }),
    [query.range, query.from, query.to, tick]
  );
  if (loading) return <div className="cc-skeleton" style={{ height: 260 }} />;
  if (error || !data) return <div className="cc-empty">加载失败。 <button className="cc-back" onClick={reload}>重试</button></div>;

  // The axis spans the window the backend actually computed (clamped/bucketed),
  // so a brush-zoom and pan read against real wall-clock time. Pan bounds run a
  // little wider than the current window so the overview can move into the past.
  const axisFrom = data.window.from;
  const axisTo = data.window.to;
  const span = Math.max(RANGE_MS[range], axisTo - axisFrom);
  const boundsTo = effectiveWindow.to;
  const boundsFrom = boundsTo - span * 4;

  // Segment colours mirror STATUS_LEGEND (semantic: 2xx green / 4xx amber / 5xx red).
  const stacked = data.statusClasses.map((b) => ({ ts: b.ts, segments: [
    { value: b.c2xx, color: STATUS_LEGEND[0].color },
    { value: b.c4xx, color: STATUS_LEGEND[1].color },
    { value: b.c5xx, color: STATUS_LEGEND[2].color }
  ] }));

  // Request numbers for the line chart (the shape alone doesn't say how many).
  const reqPeak = data.series.reduce((m, s) => Math.max(m, s.count), 0);
  const reqLast = data.series.at(-1)?.count ?? 0;

  // Status-code totals across the window, for the legend + percentages.
  const counts = {
    c2xx: data.statusClasses.reduce((a, b) => a + b.c2xx, 0),
    c4xx: data.statusClasses.reduce((a, b) => a + b.c4xx, 0),
    c5xx: data.statusClasses.reduce((a, b) => a + b.c5xx, 0)
  };
  const statusTotal = counts.c2xx + counts.c4xx + counts.c5xx;
  const pct = (n: number): number => (statusTotal > 0 ? Math.round((n / statusTotal) * 100) : 0);

  return (
    <div className="cc-grid cc-grid-2">
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}>
        <CardTitle>请求</CardTitle>
        <div style={summaryStyle}>
          共 {fmtCount(data.totalRequests)} 次 · 峰值 {fmtCount(reqPeak)} 次/{fmtBucketUnit(data.window.bucketMs)} · 最近 {fmtCount(reqLast)} 次/{fmtBucketUnit(data.window.bucketMs)} · 拖动图表可放大时间段
        </div>
        <LineChart
          points={data.series.map((s) => ({ ts: s.ts, value: s.count }))}
          from={axisFrom}
          to={axisTo}
          formatY={fmtCount}
          onZoom={(f, t) => setWin({ from: f, to: t })}
        />
        <RangeOverview
          from={axisFrom}
          to={axisTo}
          boundsFrom={boundsFrom}
          boundsTo={boundsTo}
          onChange={(f, t) => setWin({ from: f, to: t })}
        />
        {win !== null && (
          <button className="cc-back" style={{ marginTop: "var(--space-2)" }} onClick={() => setWin(null)}>重置时间范围</button>
        )}
      </div>
      <div className="cc-card"><CardTitle>延迟</CardTitle>
        <div className="cc-grid-stats">
          <StatCard label="p50" value={fmtMs(data.latency.p50)} />
          <StatCard label="p95" value={fmtMs(data.latency.p95)} />
          <StatCard label="p99" value={fmtMs(data.latency.p99)} />
          <StatCard label="总计" value={fmtCount(data.totalRequests)} />
        </div>
      </div>
      <div className="cc-card"><CardTitle>状态码</CardTitle>
        <div style={summaryStyle}>共 {fmtCount(statusTotal)} 次响应</div>
        <StackedBar buckets={stacked} from={data.window.from} to={data.window.to} formatY={fmtCount} />
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-3)", marginTop: "var(--space-2)", fontSize: "var(--text-caption)", color: "var(--text-secondary)" }}>
          {STATUS_LEGEND.map((s) => (
            <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: s.color, flex: "0 0 auto" }} aria-hidden="true" />
              {s.label} <strong style={{ fontVariantNumeric: "tabular-nums" }}>{fmtCount(counts[s.key])}</strong>
              <span style={{ color: "var(--text-tertiary)" }}>({pct(counts[s.key])}%)</span>
            </span>
          ))}
        </div>
      </div>
      <div className="cc-card"><CardTitle>热门接口</CardTitle>
        <BarChart data={data.topEndpoints.map((e) => ({ label: `${e.method} ${e.path}`, value: e.count }))} />
      </div>
      <div className="cc-card" style={{ gridColumn: "1 / -1" }}><CardTitle>高峰时段</CardTitle><Heatmap cells={data.heatmap} /></div>
    </div>
  );
}
